import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Award, Activity, Target, ArrowRight } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import TrendIndicator from '../../components/ui/TrendIndicator';
import MiniSparkline from '../../components/ui/MiniSparkline';
import { formatPercentOneDecimal } from '../../lib/utils';

export default function KOLAnalytics() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Mock trend data
  const reachTrend = [45, 48, 52, 55, 58, 62, 65];
  const engagementTrend = [5.5, 5.8, 6.0, 6.2, 6.5, 6.7, 7.0];
  const invitationsTrend = [8, 10, 12, 14, 16, 18, 20];
  const completionTrend = [70, 72, 75, 78, 80, 82, 85];

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/kol');
      setData(response.data);
    } catch (error) {
      console.error('Load analytics error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return <div className="space-y-6">
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    </div>;
  }

  return (
    <div className="space-y-6 text-glass-text">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-green-600" />
            My Performance
          </h1>
          <p className="text-sm text-gray-600 mt-1">Track your engagement and earnings</p>
        </div>
        <button className="px-4 py-2 text-sm font-semibold text-green-600 hover:bg-green-50 rounded-lg transition-colors inline-flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Download Report
        </button>
      </div>

      {/* Grouped KPI Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Card 1: Reach & Engagement */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:divide-x divide-gray-200">
              
              {/* Total Reach */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Total Reach</p>
                  </div>
                  <TrendIndicator value={24.8} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-extrabold text-gray-900">
                    {(data.profile.totalReach / 1000).toFixed(1)}K
                  </p>
                </div>
                
                <p className="text-sm text-blue-600 font-semibold mt-2">
                  {data.profile.platformCount} platforms
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={reachTrend} color="#3B82F6" height={40} />
                  <p className="text-xs text-gray-500 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-gray-600">Across all platforms</span>
                  <span className="text-xs text-blue-600 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    Details <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
              {/* Avg Engagement */}
              <div className="sm:pl-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Activity className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Engagement</p>
                  </div>
                  <TrendIndicator value={16.5} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-extrabold text-gray-900">{data.profile.avgEngagement}%</p>
                </div>
                
                <p className="text-sm text-green-600 font-semibold mt-2">
                  ↑ +0.8% from last month
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={engagementTrend} color="#10B981" height={40} />
                  <p className="text-xs text-gray-500 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-gray-600">Average rate</span>
                  <span className="text-xs text-green-600 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    Analyze <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Invitations & Completion */}
        <Card className="shadow-lg hover:shadow-xl transition-shadow">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:divide-x divide-gray-200">
              
              {/* Invitations */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Target className="w-5 h-5 text-purple-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Invitations</p>
                  </div>
                  <TrendIndicator value={30.2} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-extrabold text-gray-900">{data.invitations.total}</p>
                  <p className="text-lg text-gray-500 font-medium">total</p>
                </div>
                
                <p className="text-sm text-purple-600 font-semibold mt-2">
                  {formatPercentOneDecimal(data.invitations.acceptanceRate)} accepted
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={invitationsTrend} color="#9333EA" height={40} />
                  <p className="text-xs text-gray-500 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-gray-600">{data.invitations.byStatus['PENDING'] || 0} pending</span>
                  <span className="text-xs text-purple-600 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    View <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
              {/* Completion Rate */}
              <div className="sm:pl-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Award className="w-5 h-5 text-orange-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">Completion</p>
                  </div>
                  <TrendIndicator value={12.3} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-extrabold text-gray-900">{formatPercentOneDecimal(data.submissions.completionRate)}</p>
                </div>
                
                <p className="text-sm text-orange-600 font-semibold mt-2">
                  {data.submissions.total} submissions
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={completionTrend} color="#F59E0B" height={40} />
                  <p className="text-xs text-gray-500 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-gray-600">{data.submissions.byStatus['APPROVED'] || 0} approved</span>
                  <span className="text-xs text-orange-600 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    View <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
            </div>
          </CardContent>
        </Card>
        
      </div>

      {/* Platform Stats */}
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-bold">Platform Statistics</CardTitle>
            <TrendIndicator value={18.5} label="avg growth" />
          </div>
        </CardHeader>
        <CardContent>
          {data.platforms.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.platforms.map((platform: any) => (
                <div key={platform.platform} className="p-5 bg-gradient-to-br from-blue-50 via-purple-50 to-green-50 rounded-xl shadow hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-bold capitalize text-gray-900">{platform.platform}</p>
                    <Badge variant="outline" className="font-semibold">@{platform.handle}</Badge>
                  </div>
                  <p className="text-4xl font-extrabold text-gray-900 mb-2">
                    {(platform.followers / 1000).toFixed(1)}K
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 font-semibold">
                      {formatPercentOneDecimal(platform.engagementRate)} engagement
                    </p>
                    <TrendIndicator value={12.5} showValue={false} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No platform data</p>
          )}
        </CardContent>
      </Card>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Invitations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(data.invitations.byStatus).map(([status, count]: [string, any]) => (
                <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Badge variant={
                    status === 'ACCEPTED' ? 'success' :
                    status === 'PENDING' ? 'warning' :
                    status === 'REJECTED' ? 'destructive' : 'default'
                  }>
                    {status}
                  </Badge>
                  <span className="text-lg font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Scripts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(data.scripts.byStatus).map(([status, count]: [string, any]) => (
                <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Badge variant={
                    status === 'APPROVED_BY_BRAND' ? 'success' :
                    status === 'SUBMITTED_FOR_BRAND' ? 'warning' : 'default'
                  }>
                    {status === 'APPROVED_BY_BRAND' ? 'APPROVED' : status}
                  </Badge>
                  <span className="text-lg font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(data.submissions.byStatus).map(([status, count]: [string, any]) => (
                <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Badge variant={
                    status === 'APPROVED' ? 'success' :
                    status === 'SUBMITTED' ? 'warning' : 'default'
                  }>
                    {status}
                  </Badge>
                  <span className="text-lg font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl font-bold flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Recent Invitations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.recentActivity.length > 0 ? (
            <div className="space-y-3">
              {data.recentActivity.map((activity: any) => (
                <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{activity.jobTitle}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {new Date(activity.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={
                    activity.status === 'ACCEPTED' ? 'success' :
                    activity.status === 'PENDING' ? 'warning' :
                    activity.status === 'REJECTED' ? 'destructive' : 'default'
                  }>
                    {activity.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No recent activity</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
